import { apiClient } from '@indipia/shared';
import type { ApiResponse } from '@indipia/shared-types';
import type { ShareLink } from '@indipia/shared-features';
import { API_PRX } from './endpoints';

// 사진 공유 화면(발송·이력)이 쓰는 서버 연결을 한 곳에 모은 어댑터.
// 서버 신규 API 가 확정되면 이 파일의 경로·필드만 맞추면 된다.

// ── [공유하기] 고객 고정 링크 — GET /schedule/sites/{siteId}/customer-share-link (현장 기준) ──
// 스케줄 서비스가 발급하는 고객 링크를 사진 화면에서도 같은 현장 기준으로 조회한다.
export async function fetchPhotoShareLink(siteId: number): Promise<ShareLink> {
  const res = await apiClient.get<ApiResponse<{ url: string; expiresNotice: string }>>(
    `/schedule/sites/${siteId}/customer-share-link`,
  );
  const { url, expiresNotice } = res.data.data;
  return { url, expiresNotice };
}

// ── 알림톡 미리보기 — POST /photo/shares/preview (51-Photo PhotoShareSendController) ──
// 문구·버튼은 서버가 Hermes 렌더 결과로 내려준다. 화면은 대상만 보내고 문구를 조립하지 않는다.
// expiresAt: CUSTOMER 는 서버(Planner) 산정 만료, GENERAL 은 요청 expiresAt 에코 — 화면은 날짜를 계산하지 않고 이 값만 표시한다.
export type SharePreviewGroup = 'CUSTOMER' | 'GENERAL';

export interface PhotoSharePreviewRequest {
  group: SharePreviewGroup;
  siteId: number;
  /** 선택 장수(미리보기 문구용, 0 이상) — 생략하면 서버가 현장 사진 수를 센다 */
  photoCount?: number;
  recipient: { name: string; recipientType: string };
  /** GENERAL 필수 */
  title?: string;
  content?: string;
  /** GENERAL 필수 — 'YYYY-MM-DDTHH:mm:ss' */
  expiresAt?: string;
}

export interface SharePreviewButton { type: string; name: string; url: string | null }

export interface SharePreviewResponse {
  group: SharePreviewGroup;
  title: string | null;
  body: string;
  buttons: SharePreviewButton[];
  expiresAt: string | null;
  failoverBody: string | null;
}

export async function previewPhotoShare(req: PhotoSharePreviewRequest, signal?: AbortSignal): Promise<SharePreviewResponse> {
  const res = await apiClient.post<ApiResponse<SharePreviewResponse>>(`${API_PRX.PHOTO}/shares/preview`, req, { signal });
  return res.data.data;
}

/** 서버 오류 응답의 메시지 — 없으면 일반 문구 */
export function previewErrorMessage(err: unknown): string {
  const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
  return msg || '미리보기를 불러오지 못했습니다.';
}

// ── 공유취소 영향 조회 — GET /photo/share-bundles/{shareId}/revoke-impact (51-Photo ShareBundleController, 서버 구현 확인) ──
// 응답 필드는 서버 ShareBundleDto.RevokeImpactResponse 와 같다.
export interface RevokeImpactMenu { menu: string; historyCount: number }

export interface RevokeImpact {
  shareKey: string | null;
  shareId: number | null;
  linkActive: boolean;
  sameLinkHistoryCount: number;
  viewCount: number;
  lastViewedDtm: string | null;
  otherMenus: RevokeImpactMenu[];
}

export async function fetchRevokeImpact(shareId: number, signal?: AbortSignal): Promise<RevokeImpact> {
  const res = await apiClient.get<ApiResponse<RevokeImpact>>(
    `${API_PRX.PHOTO}/share-bundles/${shareId}/revoke-impact`,
    { signal },
  );
  return res.data.data;
}

// ── 그룹 재발송 — POST /share-bundles/batch/{shareBatchId}/resend (서버 기존, ShareBundleController) ──
// 응답은 발송 응답(ShareSendResponse)과 같다. 취소·만료 제외 원 수신자에게 새 링크로 다시 보낸다.
export interface PhotoResendResult {
  shareBatchId?: string;
  dispatchBatchId?: string;
  sentCount?: number;
  failedCount?: number;
}

export async function resendPhotoShareBatch(shareBatchId: string): Promise<PhotoResendResult> {
  const res = await apiClient.post<ApiResponse<PhotoResendResult>>(
    `${API_PRX.PHOTO}/share-bundles/batch/${encodeURIComponent(shareBatchId)}/resend`,
    null,
  );
  return res.data.data ?? {};
}

/** 서버 오류 응답의 { status, code, message } — 없으면 빈 값 */
export function apiErrorInfo(err: unknown): { status?: number; code?: string; message?: string } {
  const r = (err as { response?: { status?: number; data?: { code?: string; message?: string } } })?.response;
  return { status: r?.status, code: r?.data?.code, message: r?.data?.message };
}
